import {
  Badge,
  createStyles,
  ListItemIcon,
  MenuItem,
  MenuList,
  type WithStyles,
  withStyles,
} from "@material-ui/core";
import NotificationsNone from "@mui/icons-material/NotificationsNone";
import { useTranslations } from "adapters/i18n";
import {
  MenuButton,
  MenuIconButtonTrigger,
  useMenuButton,
} from "features/shared/components";
import React, {
  type ChangeEvent,
  type MouseEvent,
  type RefObject,
} from "react";
import type { NotificationItem } from "../notification.types";

const ID = "notification-center";

export type NotifierProps = {
  badgeCount: number;
  items: NotificationItem[];
} & WithStyles<typeof styles>;

const Notifier = ({ badgeCount, classes, items = [] }: NotifierProps) => {
  const { handleClickAway, handleToggleMenu, isMenuOpen, triggerRef } =
    useMenuButton();

  const count = badgeCount != null ? badgeCount : items.length;

  const handleActionItem = (event: MouseEvent, item: NotificationItem) => {
    item.actionOnShow?.();
    item.action?.();
    handleClickAway(event as unknown as ChangeEvent<HTMLElement>);
  };

  const shouldOpen = isMenuOpen && items.length > 0;

  return (
    <MenuButton
      id={ID}
      isOpen={shouldOpen}
      onClickAway={handleClickAway}
      placement="bottom"
      trigger={
        <NotifyButton
          classes={classes}
          count={count}
          isMenuOpen={shouldOpen}
          onClick={handleToggleMenu}
          triggerRef={triggerRef}
        />
      }
      triggerRef={triggerRef}
    >
      <MenuList>
        {items.map((item) => (
          <MenuItem
            key={item.id}
            onClick={(event) => handleActionItem(event, item)}
            className={classes.menuItem}
          >
            <div className={classes.menuItemTitle}>
              {item.icon != null && <ListItemIcon>{item.icon}</ListItemIcon>}
              {item.title}
            </div>
            {item.description != null && (
              <span className={classes.menuItemDescription}>
                {item.description}
              </span>
            )}
          </MenuItem>
        ))}
      </MenuList>
    </MenuButton>
  );
};

type NotifyButtonProps = {
  count: number;
  isMenuOpen: boolean;
  onClick: () => void;
  triggerRef: RefObject<HTMLButtonElement>;
} & WithStyles<typeof styles>;

const NotifyButton = ({
  classes,
  count,
  isMenuOpen,
  onClick,
  triggerRef,
}: NotifyButtonProps) => {
  const { t } = useTranslations();

  return (
    <MenuIconButtonTrigger
      aria-label={t("common:notification-center")}
      className={classes.button}
      isListOpen={isMenuOpen}
      listId={ID}
      onClick={onClick}
      ref={triggerRef}
    >
      <Badge badgeContent={count} classes={{ badge: classes.counter }}>
        <NotificationsNone className={classes.icon} />
      </Badge>
    </MenuIconButtonTrigger>
  );
};

const styles = () =>
  createStyles({
    button: {
      height: "100%",
      width: "auto",
    },
    icon: {
      fill: "#616161",
      fontSize: "24px !important",
    },
    counter: {
      backgroundColor: "#EB5757",
      color: "#fff",
    },
    menuItem: {
      flexDirection: "column",
      alignItems: "flex-start",
      height: "auto",
      maxWidth: 250,
    },
    menuItemTitle: {
      alignItems: "center",
      display: "flex",
      flexDirection: "row",
    },
    menuItemDescription: {
      marginTop: 8,
      textWrap: "auto",
      color: "#757575",
    },
  });

export default withStyles(styles)(Notifier);
